import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Play, Pause, RotateCcw, X, Minus, Plus, FlipHorizontal,
  Video, VideoOff, Circle, Square, Download, Loader2, Trash2, Save, ZoomIn, ZoomOut,
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const CHUNK_SIZE = 2 * 1024 * 1024;

const pickMime = () =>
  ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm']
    .find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || '';

const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export const TeleprompterMode = ({ script, page, onVideoSaved, onClose }) => {
  const [playing, setPlaying] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [speed, setSpeed] = useState(() => Number(localStorage.getItem('tp_speed')) || 55);
  const [fontSize, setFontSize] = useState(() => Number(localStorage.getItem('tp_font')) || 42);
  const [mirror, setMirror] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  const [uploadPct, setUploadPct] = useState(null);
  const [reviewCircle, setReviewCircle] = useState(false);
  const [zoom, setZoom] = useState(() => Number(localStorage.getItem('tp_zoom')) || 1);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const boxRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(null);
  const posRef = useRef(0);
  const speedRef = useRef(speed);
  speedRef.current = speed;
  const playingRef = useRef(false);
  playingRef.current = playing || countdown != null;
  const reviewingRef = useRef(false);
  reviewingRef.current = recordedBlob != null;
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const camRef = useRef(null);
  const rawStreamRef = useRef(null);
  const rawVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const drawRafRef = useRef(null);
  const pendingRecordRef = useRef(false);

  useEffect(() => { localStorage.setItem('tp_zoom', String(zoom)); }, [zoom]);

  useEffect(() => { localStorage.setItem('tp_speed', String(speed)); }, [speed]);
  useEffect(() => { localStorage.setItem('tp_font', String(fontSize)); }, [fontSize]);

  useEffect(() => {
    if (!playing) { lastRef.current = null; cancelAnimationFrame(rafRef.current); return; }
    const el = boxRef.current;
    if (el) posRef.current = el.scrollTop;
    const step = (ts) => {
      const box = boxRef.current;
      if (box) {
        if (lastRef.current != null) {
          posRef.current += (speedRef.current * (ts - lastRef.current)) / 1000;
          box.scrollTop = posRef.current;
          if (box.scrollTop + box.clientHeight >= box.scrollHeight - 2) { setPlaying(false); return; }
        }
        lastRef.current = ts;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  const togglePlay = useCallback(() => {
    if (reviewingRef.current) return;
    if (playingRef.current) { setPlaying(false); setCountdown(null); pendingRecordRef.current = false; }
    else setCountdown(3);
  }, []);

  const discardOnStopRef = useRef(false);

  const startRecorder = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = pickMime();
    let rec;
    try { rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 2500000, audioBitsPerSecond: 96000 }); }
    catch { rec = new MediaRecorder(stream); }
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      if (discardOnStopRef.current) { discardOnStopRef.current = false; chunksRef.current = []; return; }
      const type = rec.mimeType || mime || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
    };
    rec.start(1000);
    recorderRef.current = rec;
    setRecording(true);
    setRecordSecs(0);
  }, []);

  useEffect(() => {
    if (countdown == null) return undefined;
    if (countdown === 0) {
      setCountdown(null);
      if (pendingRecordRef.current) { pendingRecordRef.current = false; startRecorder(); }
      setPlaying(true);
      return undefined;
    }
    const t = setTimeout(() => setCountdown(c => (c == null ? null : c - 1)), 800);
    return () => clearTimeout(t);
  }, [countdown, startRecorder]);

  const handleRestart = useCallback(() => {
    setPlaying(false);
    setCountdown(null);
    pendingRecordRef.current = false;
    posRef.current = 0;
    if (boxRef.current) boxRef.current.scrollTop = 0;
  }, []);

  const retake = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      discardOnStopRef.current = true;
      try { recorderRef.current.stop(); } catch {}
    }
    setRecording(false);
    handleRestart();
    pendingRecordRef.current = true;
    setCountdown(3);
  }, [handleRestart]);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(drawRafRef.current);
    rawStreamRef.current?.getTracks().forEach(t => t.stop());
    rawStreamRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const toggleCamera = async () => {
    if (cameraOn) {
      stopCamera();
      setCameraOn(false);
      return;
    }
    try {
      const raw = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      rawStreamRef.current = raw;
      // Zoom pipeline: raw camera -> hidden video -> canvas (cropped by zoom) -> recorded stream.
      const vid = rawVideoRef.current;
      const canvas = canvasRef.current;
      vid.srcObject = raw;
      await vid.play().catch(() => {});
      const ctx = canvas.getContext('2d');
      const draw = () => {
        const vw = vid.videoWidth, vh = vid.videoHeight;
        if (vw && vh) {
          if (canvas.width !== vw || canvas.height !== vh) { canvas.width = vw; canvas.height = vh; }
          const z = Math.max(1, zoomRef.current);
          const sw = vw / z, sh = vh / z;
          const maxX = (vw - sw) / 2, maxY = (vh - sh) / 2;
          const sx = maxX + panRef.current.x * maxX;
          const sy = maxY + panRef.current.y * maxY;
          ctx.drawImage(vid, sx, sy, sw, sh, 0, 0, vw, vh);
        }
        drawRafRef.current = requestAnimationFrame(draw);
      };
      draw();
      const out = canvas.captureStream(30);
      raw.getAudioTracks().forEach(t => out.addTrack(t));
      streamRef.current = out;
      setCameraOn(true);
    } catch {
      toast.error('Camera unavailable — allow camera & microphone access in your browser settings');
    }
  };

  const clampPan = (v) => Math.max(-1, Math.min(1, v));

  const onPanStart = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };

  const onPanMove = (e) => {
    if (!dragRef.current) return;
    const vid = rawVideoRef.current;
    const vw = vid?.videoWidth, vh = vid?.videoHeight;
    if (!vw || !vh) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const z = Math.max(1, zoomRef.current);
    const srcPerPx = Math.min(vw, vh) / z / rect.width;
    const maxX = (vw - vw / z) / 2, maxY = (vh - vh / z) / 2;
    const dx = e.clientX - dragRef.current.x, dy = e.clientY - dragRef.current.y;
    dragRef.current = { x: e.clientX, y: e.clientY };
    const p = panRef.current;
    if (maxX > 0) p.x = clampPan(p.x + (dx * srcPerPx) / maxX); // preview is mirrored -> +dx
    if (maxY > 0) p.y = clampPan(p.y - (dy * srcPerPx) / maxY);
  };

  const onPanEnd = () => { dragRef.current = null; };

  const resetPan = () => { panRef.current = { x: 0, y: 0 }; };

  useEffect(() => {
    if (cameraOn && camRef.current && streamRef.current) camRef.current.srcObject = streamRef.current;
  }, [cameraOn, recordedBlob]);

  const toggleRecord = useCallback(() => {
    if (recording) {
      try { recorderRef.current?.stop(); } catch {}
      setRecording(false);
      setPlaying(false);
      return;
    }
    if (!streamRef.current) { toast.error('Turn on the camera first'); return; }
    handleRestart();
    pendingRecordRef.current = true;
    setCountdown(3);
  }, [recording, handleRestart]);

  useEffect(() => {
    if (!recording) return undefined;
    const iv = setInterval(() => setRecordSecs(s => s + 1), 1000);
    return () => clearInterval(iv);
  }, [recording]);

  const discardRecording = useCallback(() => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl('');
  }, [recordedUrl]);

  const reRecord = () => {
    discardRecording();
    pendingRecordRef.current = true;
    handleRestart();
    setCountdown(3);
  };

  const recordingFilename = () => {
    const ext = (recordedBlob?.type || '').includes('mp4') ? 'mp4' : 'webm';
    const base = (page?.company_name || 'teleprompter').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'video';
    return `video-${base}.${ext}`;
  };

  const downloadRecording = () => {
    const a = document.createElement('a');
    a.href = recordedUrl;
    a.download = recordingFilename();
    a.click();
  };

  const saveToLandingPage = async () => {
    if (!page || !recordedBlob) return;
    if (recordedBlob.size > 200 * 1024 * 1024) { toast.error('Video too large (max 200MB)'); return; }
    setUploadPct(0);
    try {
      const totalChunks = Math.ceil(recordedBlob.size / CHUNK_SIZE) || 1;
      const init = await api.initChunkedProject(recordingFilename(), totalChunks);
      const uploadId = init.data.upload_id;
      for (let i = 0; i < totalChunks; i++) {
        const blob = recordedBlob.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        await api.uploadChunk(uploadId, i, blob);
        setUploadPct(Math.round(((i + 1) / totalChunks) * 95));
      }
      await api.completeLandingVideo(page.id, uploadId);
      setUploadPct(null);
      toast.success('Video saved — the landing page now shows your recording');
      discardRecording();
      onVideoSaved?.();
    } catch (e) {
      setUploadPct(null);
      toast.error(e.response?.data?.detail || 'Video upload failed');
    }
  };

  useEffect(() => () => {
    cancelAnimationFrame(drawRafRef.current);
    rawStreamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current?.getTracks().forEach(t => t.stop());
    try { if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop(); } catch {}
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (reviewingRef.current) return;
      if (e.code === 'Space') { e.preventDefault(); togglePlay(); }
      else if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSpeed(s => Math.min(150, s + 5)); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setSpeed(s => Math.max(10, s - 5)); }
      else if (e.key === '+' || e.key === '=') setFontSize(f => Math.min(80, f + 2));
      else if (e.key === '-') setFontSize(f => Math.max(24, f - 2));
      else if (e.key.toLowerCase() === 'r') handleRestart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, onClose, handleRestart]);

  const ctlBtn = 'h-9 w-9 flex items-center justify-center rounded-sm border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 transition-colors';

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="teleprompter-mode">
      <div className="absolute left-0 right-0 pointer-events-none z-10" style={{ top: '32%' }}>
        <div className="border-t border-sky-500/40" />
        <div className="absolute -top-[8px] left-3 w-0 h-0 border-y-8 border-y-transparent border-l-[12px] border-l-sky-500/80" />
      </div>

      <div ref={boxRef} onClick={togglePlay} data-testid="teleprompter-scroll"
        className="flex-1 overflow-y-hidden cursor-pointer select-none px-[10vw]"
        style={{ transform: mirror ? 'scaleX(-1)' : 'none' }}>
        <div style={{ height: '32vh' }} />
        <pre className="whitespace-pre-wrap text-white text-center" style={{ fontSize: `${fontSize}px`, lineHeight: 1.65, fontFamily: 'inherit' }}>
          {script}
        </pre>
        <div style={{ height: '75vh' }} />
      </div>

      {cameraOn && !recordedBlob && (
        <div className="absolute bottom-20 right-4 z-20 flex flex-col items-center gap-1.5" data-testid="teleprompter-camera-pip">
          {recording && (
            <div className="flex items-center gap-1.5 bg-black/80 rounded px-2 py-0.5">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[11px] font-mono text-red-400" data-testid="teleprompter-rec-timer">REC {fmtSecs(recordSecs)}</span>
            </div>
          )}
          <div onPointerDown={onPanStart} onPointerMove={onPanMove} onPointerUp={onPanEnd}
            onPointerCancel={onPanEnd} onDoubleClick={resetPan}
            title="Drag to reposition the crop (zoom > 1×) · double-click to center"
            className={`w-52 h-52 rounded-full border-[5px] p-1.5 bg-black/60 shadow-2xl transition-colors cursor-move touch-none select-none ${recording ? 'border-red-600/80' : 'border-sky-800/70'}`}
            data-testid="teleprompter-circle-drag">
            <video ref={camRef} autoPlay muted playsInline
              className="w-full h-full rounded-full object-cover object-top pointer-events-none"
              style={{ transform: 'scaleX(-1)' }} />
          </div>
          <span className="text-[10px] text-zinc-400 bg-black/70 rounded px-2 py-0.5" data-testid="teleprompter-circle-hint">
            Landing page view — zoom &amp; drag to frame yourself
          </span>
          <div className="flex items-center gap-1.5 bg-black/70 rounded px-2 py-1" data-testid="teleprompter-zoom-controls">
            <button onClick={() => setZoom(z => Math.max(1, +(z - 0.2).toFixed(1)))} data-testid="teleprompter-zoom-out"
              className="text-zinc-400 hover:text-white transition-colors" title="Zoom out">
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <input type="range" min={1} max={3} step={0.1} value={zoom} data-testid="teleprompter-zoom"
              onChange={e => setZoom(Number(e.target.value))}
              className="w-24 h-1 accent-sky-500 cursor-pointer" />
            <button onClick={() => setZoom(z => Math.min(3, +(z + 0.2).toFixed(1)))} data-testid="teleprompter-zoom-in"
              className="text-zinc-400 hover:text-white transition-colors" title="Zoom in">
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <span className="text-[10px] font-mono text-sky-400 w-8 text-right">{zoom.toFixed(1)}x</span>
          </div>
        </div>
      )}

      {/* big touch controls (iPad) */}
      {!recordedBlob && (
        <div className="absolute left-4 bottom-24 z-20 flex flex-col gap-2.5" data-testid="teleprompter-big-controls">
          <button onClick={togglePlay} data-testid="teleprompter-big-pause"
            className={`h-16 w-44 flex items-center justify-center gap-2.5 rounded-md text-lg font-medium shadow-2xl border transition-colors ${
              playing || countdown != null
                ? 'bg-amber-600/90 hover:bg-amber-500 border-amber-500/60 text-white'
                : 'bg-sky-700/90 hover:bg-sky-600 border-sky-500/60 text-white'}`}>
            {playing || countdown != null ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            {playing || countdown != null ? 'Pause' : (recording ? 'Resume' : 'Start')}
          </button>
          {recording && (
            <button onClick={retake} data-testid="teleprompter-big-retake"
              className="h-16 w-44 flex items-center justify-center gap-2.5 rounded-md bg-red-700/90 hover:bg-red-600 border border-red-500/60 text-white text-lg font-medium shadow-2xl transition-colors">
              <RotateCcw className="w-6 h-6" /> Retake
            </button>
          )}
        </div>
      )}

      {/* hidden zoom pipeline elements */}
      <video ref={rawVideoRef} autoPlay muted playsInline className="absolute w-px h-px opacity-0 pointer-events-none -z-10" />
      <canvas ref={canvasRef} className="absolute w-px h-px opacity-0 pointer-events-none -z-10" />

      {countdown != null && countdown > 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20" data-testid="teleprompter-countdown">
          <span className="text-sky-400 font-heading" style={{ fontSize: 140 }}>{countdown}</span>
        </div>
      )}

      {recordedBlob && (
        <div className="absolute inset-0 z-30 bg-black/95 flex flex-col items-center justify-center gap-4 p-6" data-testid="teleprompter-review">
          <p className="text-sm text-zinc-300">Recording finished · {fmtSecs(recordSecs)} · {(recordedBlob.size / (1024 * 1024)).toFixed(1)} MB</p>
          {reviewCircle ? (
            <div className="w-64 h-64 rounded-full border-[5px] border-sky-800/70 p-1.5 bg-black">
              <video key="circle" src={recordedUrl} autoPlay loop muted playsInline
                className="w-full h-full rounded-full object-cover object-top" data-testid="teleprompter-review-circle" />
            </div>
          ) : (
            <video key="full" src={recordedUrl} controls playsInline className="max-h-[55vh] max-w-[80vw] rounded-sm border border-zinc-800" />
          )}
          <button onClick={() => setReviewCircle(c => !c)} data-testid="teleprompter-review-toggle"
            className="h-7 px-3 flex items-center gap-1.5 rounded-sm border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 text-[11px] transition-colors">
            <Circle className="w-3 h-3" /> {reviewCircle ? 'Full view' : 'Landing page view (circle)'}
          </button>
          {uploadPct != null ? (
            <div className="w-72">
              <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
                <div className="h-full bg-sky-500 transition-all" style={{ width: `${uploadPct}%` }} />
              </div>
              <p className="text-[11px] text-zinc-400 mt-1.5 text-center">Uploading… {uploadPct}%</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {page && (
                <button onClick={saveToLandingPage} data-testid="teleprompter-save-video"
                  className="h-9 px-4 flex items-center gap-2 rounded-sm bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium transition-colors">
                  <Save className="w-4 h-4" /> Save to landing page
                </button>
              )}
              <button onClick={downloadRecording} data-testid="teleprompter-download-video"
                className="h-9 px-4 flex items-center gap-2 rounded-sm border border-zinc-700 text-zinc-200 hover:text-white hover:border-zinc-500 text-sm transition-colors">
                <Download className="w-4 h-4" /> Download
              </button>
              <button onClick={reRecord} data-testid="teleprompter-rerecord"
                className="h-9 px-4 flex items-center gap-2 rounded-sm border border-zinc-700 text-zinc-200 hover:text-white hover:border-zinc-500 text-sm transition-colors">
                <RotateCcw className="w-4 h-4" /> Re-record
              </button>
              <button onClick={discardRecording} data-testid="teleprompter-discard"
                className="h-9 px-4 flex items-center gap-2 rounded-sm border border-red-900/60 text-red-400 hover:text-red-300 text-sm transition-colors">
                <Trash2 className="w-4 h-4" /> Discard
              </button>
            </div>
          )}
        </div>
      )}

      <div className="shrink-0 bg-zinc-950/95 border-t border-zinc-800 px-4 py-3 flex items-center gap-x-4 gap-y-2 flex-wrap z-10">
        <div className="flex items-center gap-2">
          <button onClick={togglePlay} data-testid="teleprompter-play"
            className="h-9 px-4 flex items-center gap-2 rounded-sm bg-sky-700 hover:bg-sky-600 text-white text-sm font-medium transition-colors">
            {playing || countdown != null ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {playing || countdown != null ? 'Pause' : 'Start'}
          </button>
          <button onClick={handleRestart} className={ctlBtn} title="Restart (R)" data-testid="teleprompter-restart">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={toggleCamera} data-testid="teleprompter-camera"
            className={`h-9 px-3 flex items-center gap-1.5 rounded-sm border text-xs transition-colors ${cameraOn ? 'border-sky-600 text-sky-400 bg-sky-950/40' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}
            title="Turn the camera on to record yourself while reading">
            {cameraOn ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />} Camera
          </button>
          {cameraOn && (
            <button onClick={toggleRecord} data-testid="teleprompter-record"
              className={`h-9 px-3 flex items-center gap-1.5 rounded-sm text-xs font-medium transition-colors ${recording ? 'bg-red-600 hover:bg-red-500 text-white' : 'border border-red-800 text-red-400 hover:text-red-300 hover:border-red-600'}`}
              title="Start recording — the text starts scrolling automatically after the countdown">
              {recording ? <Square className="w-4 h-4" /> : <Circle className="w-4 h-4 fill-red-500 text-red-500" />}
              {recording ? `Stop ${fmtSecs(recordSecs)}` : 'Record'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Speed</span>
          <input type="range" min={10} max={150} step={5} value={speed} data-testid="teleprompter-speed"
            onChange={e => setSpeed(Number(e.target.value))}
            className="w-32 h-1 accent-sky-500 cursor-pointer" />
          <span className="text-xs font-mono text-sky-400 w-8">{speed}</span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Font</span>
          <button onClick={() => setFontSize(f => Math.max(24, f - 2))} className={ctlBtn} title="Smaller (−)" data-testid="teleprompter-font-minus">
            <Minus className="w-4 h-4" />
          </button>
          <span className="text-xs font-mono text-zinc-300 w-7 text-center">{fontSize}</span>
          <button onClick={() => setFontSize(f => Math.min(80, f + 2))} className={ctlBtn} title="Larger (+)" data-testid="teleprompter-font-plus">
            <Plus className="w-4 h-4" />
          </button>
        </div>

        <button onClick={() => setMirror(m => !m)} data-testid="teleprompter-mirror"
          className={`h-9 px-3 flex items-center gap-1.5 rounded-sm border text-xs transition-colors ${mirror ? 'border-sky-600 text-sky-400 bg-sky-950/40' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}
          title="Mirror the text horizontally (for teleprompter glass rigs)">
          <FlipHorizontal className="w-4 h-4" /> Mirror
        </button>

        <span className="text-[10px] text-zinc-600 hidden xl:block">
          Space / tap = start &amp; pause · ↑ ↓ = speed · + − = font · R = restart · Esc = exit
        </span>

        <button onClick={onClose} className={`${ctlBtn} ml-auto`} title="Exit (Esc)" data-testid="teleprompter-exit">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>,
    document.body
  );
};
